import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import TableHead from "@/components/tables/TableHead";
import TagTable from "@/components/tables/TagTable";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import AddTagModal from "@/components/modals/AddTagModal";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  setPageNo,
  setDataLength,
  setSorting,
  setFilter,
  setSearchItem,
  setTags,
  resetTagsState,
  resetResponse,
  hydrateFromCache,
} from "@/state/tags/slice";
import { fetchTagsThunk, loadMoreTagsThunk } from "@/state/tags/thunks";
import { getListCacheBootstrap } from "@/utils/listPageCache";

type Props = {};

function TagsList({}: Props) {
    const dispatch = useAppDispatch();
    const {
        tags,
        isLoading,
        loadData,
        totalCount,
        tagLeft,
        allFetched,
        pageNo,
        dataLength,
        sorting,
        filter,
        searchItem,
    } = useAppSelector((state) => state.tags);

    const [showAddTagModal, setShowAddTagModal] = useState(false);
    const [isAddTagsPermitted, setIsAddTagsPermitted] = useState(true);

    useEffect(() => {
        setIsAddTagsPermitted(isFeaturePermitted("AdminPortalAddTags"));
    }, []);

    // Fetch tags when dataLength, sorting, or searchItem changes
    useEffect(() => {
        const bootstrap = getListCacheBootstrap("tags", { dataLength, sorting, searchItem, filter });
        if (bootstrap.hasCache) {
            dispatch(hydrateFromCache({ items: bootstrap.items as typeof tags, totalCount: bootstrap.totalCount }));
        }
        dispatch(setPageNo(1));
        dispatch(resetResponse());
        dispatch(fetchTagsThunk());
    }, [dataLength, sorting, searchItem, dispatch]);

    // Handle scroll for infinite loading
    useEffect(() => {
        const handleScroll = () => {
            if (allFetched || isLoading) {
                return;
            }
            if (
                window.innerHeight + document.documentElement.scrollTop >=
                document.documentElement.offsetHeight - 1
            ) {
                if (tagLeft) {
                    dispatch(loadMoreTagsThunk());
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [allFetched, isLoading, tagLeft, dispatch]);

    const search = useCallback((searchText: string) => {
        dispatch(setPageNo(1));
        dispatch(setSearchItem(searchText || null));
    }, [dispatch]);

    const handleReset = useCallback(() => {
        dispatch(resetTagsState());
        dispatch(resetResponse());
        dispatch(fetchTagsThunk());
    }, [dispatch]);

    const handleResetResponse = useCallback(() => {
        dispatch(resetResponse());
    }, [dispatch]);

    const handleSetFilter = useCallback((newFilter: any) => {
        dispatch(setFilter(newFilter));
    }, [dispatch]);

    const handleSetDataLength = useCallback((newDataLength: number) => {
        dispatch(setDataLength(newDataLength));
    }, [dispatch]);

    const handleSetSorting = useCallback((newSorting: any) => {
        dispatch(setSorting(newSorting));
    }, [dispatch]);

    const handleSetTags = useCallback((newTags: any[]) => {
        dispatch(setTags(newTags));
    }, [dispatch]);

    const handleSetPageNo = useCallback((newPageNo: number) => {
        dispatch(setPageNo(newPageNo));
    }, [dispatch]);

    // Reset data queries function for compatibility with TagTable
    const resetDataQueries = useCallback(() => {
        dispatch(resetTagsState());
    }, [dispatch]);

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                            id="tag-table"
                            handleReset={handleResetResponse}
                            pageNo={pageNo}
                            dataLength={dataLength}
                            setFilter={handleSetFilter}
                            filter={filter}
                            setDataLength={handleSetDataLength}
                            reset={handleReset}
                            search={search}
                            title="Tags"
                            currentEnvo={tags.length}
                            totalEnvo={totalCount}
                            button={
                                isAddTagsPermitted && (
                                    <Button
                                        id="add-tag-btn"
                                        onClick={() => setShowAddTagModal(true)}
                                    >
                                        {BUTTONS.ADD}
                                    </Button>
                                )
                            }
                        />
            </div>
            <TagTable
                id="template-table"
                reset={handleReset}
                title="Tags"
                setTags={handleSetTags}
                search={search}
                isLoading={isLoading}
                dataLength={dataLength}
                sorting={sorting}
                setSorting={handleSetSorting}
                searchItem={searchItem || ""}
                allFetched={allFetched}
                tags={tags}
                dataLoad={loadData}
                resetDataQueries={resetDataQueries}
                setPageNo={handleSetPageNo}
                loadingData={isLoading}
            />
            <AddTagModal show={showAddTagModal} onHide={() => setShowAddTagModal(false)} />
        </div>
    );
}

export default TagsList;
